/**
 * 도면 뷰어의 <b>확장자 안내</b> 정본 — 판정·accept·화면 문구가 전부 여기서 나온다.
 *
 * <h2>왜 한 파일로 모았나</h2>
 * 형식 목록이 세 곳에 흩어져 있었다: 확장자 판정(`viewerKindOf`), 파일 선택 대화상자의 `accept`,
 * 그리고 화면에 적힌 안내 문구. 형식을 하나 더할 때 셋을 같이 고쳐야 하고, 실제로 하나를 빠뜨리면
 * <b>화면은 "엽니다" 라고 적혀 있는데 눌러도 안 열리는</b> 상태가 된다 — 사용자에게 가장 나쁜 종류의 거짓말이다.
 *
 * 이제 판정·`accept`·안내가 전부 이 배열에서 나온다. 형식을 더하려면 여기 한 줄만 더한다.
 * (같은 규율: `indexable.ts` — 화면과 워커의 판단이 갈리지 않게 목록을 한쪽에 맞춰 둔다.)
 */

/** 파일이 뷰어에서 어떻게 열리는지. dwg 는 백엔드 변환을 거쳐 열 때 dxf 로 바뀐다. */
export type ViewerKind = 'image' | 'model' | 'dxf' | 'pdf' | 'dwg';

export type FormatGroup = {
	kind: ViewerKind;
	/** 사용자에게 보이는 묶음 이름. */
	label: string;
	extensions: string[];
	/** 이 묶음이 무엇인지 한 줄. 화면 안내에 그대로 나간다. */
	note: string;
	/** 내 컴퓨터에서 바로 열 수 있는가. dwg 는 서버 변환이 필요해 불가. */
	local: boolean;
};

export const VIEWER_FORMATS: FormatGroup[] = [
	{
		kind: 'dwg',
		label: 'CAD 도면 (AutoCAD)',
		extensions: ['dwg'],
		note: '서버가 DXF 로 변환해 엽니다.',
		local: false,
	},
	{
		kind: 'dxf',
		label: 'CAD 도면 (교환 형식)',
		extensions: ['dxf'],
		note: '선·글자·치수·블록·레이어를 그립니다. 해칭 채움과 선종류(파선)는 아직 표시되지 않습니다.',
		local: true,
	},
	{
		kind: 'pdf',
		label: 'PDF 문서',
		extensions: ['pdf'],
		note: '여러 장이면 넘겨 봅니다. 확대할 때마다 다시 그려 선이 뭉개지지 않습니다.',
		local: true,
	},
	{
		kind: 'model',
		label: '3D 모델 · BIM',
		extensions: ['ifc', 'glb', 'gltf', 'obj'],
		note: 'IFC 는 부재 선택·속성·단면까지 됩니다. gltf 는 텍스처가 따로라 glb 로 내보내는 편이 안전합니다.',
		local: true,
	},
	{
		kind: 'image',
		label: '이미지',
		extensions: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'],
		note: '스캔 도면·사진. 휠로 확대, 드래그로 이동합니다.',
		local: true,
	},
];

/** 확장자 → 뷰어 처리 방식. 목록에 없으면 null(= 이 뷰어로는 못 연다). */
export function viewerKindOf(name: string): ViewerKind | null {
	const ext = name.split('.').pop()?.toLowerCase() ?? '';
	if (!name.includes('.')) return null;
	return VIEWER_FORMATS.find((group) => group.extensions.includes(ext))?.kind ?? null;
}

/** 파일 선택 대화상자의 `accept`. 내 컴퓨터에서 못 여는 형식(dwg)은 뺀다. */
export function localAcceptAttribute(): string {
	const extensions = VIEWER_FORMATS.filter((g) => g.local).flatMap((g) => g.extensions.map((e) => `.${e}`));
	return [...extensions, 'image/*'].join(',');
}

/** "glb·gltf·obj·ifc·dxf·pdf·이미지" 같은 한 줄 요약. 안내 문구에 쓴다. */
export function localFormatSummary(): string {
	return VIEWER_FORMATS.filter((g) => g.local)
		.flatMap((g) => g.extensions)
		.join('·');
}
